import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ReactComponent as PdfIcon } from "../../../assets/svg/view/pdf.svg";
import { ReactComponent as ImageIcon } from "../../../assets/svg/view/img.svg";
import "../../../assets/scss/components/ui/modal/MultiDownloadModal.scss";
import MelisaImg from "../../../assets/img/melisa.png";

// Constantes de configuración del sistema
const MODAL_CONFIG = {
  ANIMATION_DURATION: 300,
  MAX_DOCUMENTS_DISPLAY: 100,
  SUPPORTED_FORMATS: ['.pdf', '.jpg', '.jpeg', '.png', '.gif'],
  SECTION_LABELS: {
    PERSONAL: 'Documentos Personales', // Mantenemos el nombre del primer código
    OTHER: 'Otros Documentos' // Mantenemos el nombre del primer código
  }
};

// Funciones auxiliares optimizadas
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const validateDocument = (doc) => {
  return doc && 
         doc.id && 
         doc.file_name && 
         doc.file_path &&
         MODAL_CONFIG.SUPPORTED_FORMATS.some(format => 
           doc.file_name.toLowerCase().endsWith(format)
         );
};

/**
 * MultiDownloadModal - Modal avanzado para descarga múltiple de documentos vehiculares
 * Implementa drag & drop, filtrado inteligente y gestión de contexto vehicular
 */
function MultiDownloadModal({
  open,
  onClose,
  personalDocuments = [],
  certificates = [],
  otherDocuments = [],
  selectedDocs,
  setSelectedDocs,
  onDownloadAll,
  userName,
  isVehicle = false,
  vehicleContext = null
}) {

  // Estados locales optimizados con inicialización lazy
  const [personalDocs, setPersonalDocs] = useState([]);
  const [othersDocs, setOthersDocs] = useState([]);
  const [personalFilesOpen, setPersonalFilesOpen] = useState(true);
  const [otherFilesOpen, setOtherFilesOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Memoización de documentos válidos para optimización
  const validatedDocuments = useMemo(() => {
    const validPersonal = personalDocuments.filter(validateDocument);
    const validCertificates = certificates.filter(validateDocument);
    const validOthers = otherDocuments.filter(validateDocument);
    
    const combinedOthers = [...validCertificates, ...validOthers];
    
    return {
      personal: validPersonal,
      others: combinedOthers,
      total: validPersonal.length + combinedOthers.length
    };
  }, [personalDocuments, certificates, otherDocuments, vehicleContext]);

  // Efecto para inicialización y gestión de estado del modal
  useEffect(() => {
    if (open && !isInitialized) {
      // Configurar documentos validados
      setPersonalDocs([...validatedDocuments.personal]);
      setOthersDocs([...validatedDocuments.others]);
      
      // Configurar estado inicial de secciones
      setPersonalFilesOpen(validatedDocuments.personal.length > 0);
      setOtherFilesOpen(false);
      
      setIsInitialized(true);
    }
    
    // Cleanup al cerrar modal
    if (!open && isInitialized) {
      setIsInitialized(false);
    }
  }, [open, validatedDocuments, setSelectedDocs, isInitialized]);

  // Efecto para actualizar documentos cuando cambian las props
  useEffect(() => {
    if (isInitialized) {
      setPersonalDocs([...validatedDocuments.personal]);
      setOthersDocs([...validatedDocuments.others]);
      
      // Limpiar selección si los documentos cambiaron
      const currentDocIds = [
        ...validatedDocuments.personal.map(doc => doc.id),
        ...validatedDocuments.others.map(doc => doc.id)
      ];
      
      setSelectedDocs(prev => prev.filter(id => currentDocIds.includes(id)));
    }
  }, [validatedDocuments, isInitialized, setSelectedDocs]);

  // Función optimizada para obtener icono de archivo
  const getFileIcon = useCallback((doc) => {
    if (!doc?.file_name) return null;

    const lowerCaseName = doc.file_name.toLowerCase();
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(lowerCaseName);
    const isPdf = lowerCaseName.endsWith('.pdf');

    if (isImage) return <ImageIcon />;
    if (isPdf) return <PdfIcon />;
    
    return null;
  }, []);

  // Handler optimizado para checkbox con validación
  const handleCheckboxChange = useCallback((docId, checked) => {
    if (!docId) return;
    
    if (checked) {
      setSelectedDocs(prev => {
        if (prev.includes(docId)) return prev;
        return [...prev, docId];
      });
    } else {
      setSelectedDocs(prev => prev.filter(id => id !== docId));
    }
  }, [setSelectedDocs]);

  // Handler para seleccionar/deseleccionar todos con validación
  const handleToggleSelectAll = useCallback((checked) => {
    const allValidDocIds = [
      ...personalDocs.map(doc => doc.id),
      ...othersDocs.map(doc => doc.id)
    ].filter(Boolean);

    if (checked) {
      setSelectedDocs(allValidDocIds);
    } else {
      setSelectedDocs([]);
    }
  }, [personalDocs, othersDocs, setSelectedDocs]);

  // Sistema de drag & drop optimizado
  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Validar que el movimiento es válido
    if (source.droppableId === destination.droppableId && 
        source.index === destination.index) {
      return;
    }

    // Reordenar dentro de la misma sección
    if (source.droppableId === destination.droppableId) {
      switch (source.droppableId) {
        case "personalDocs":
          setPersonalDocs(prev => reorder(prev, source.index, destination.index));
          break;
        case "othersDocs":
          setOthersDocs(prev => reorder(prev, source.index, destination.index));
          break;
        default:
          console.warn('Droppable ID no reconocido:', source.droppableId);
      }
    }
  }, []);

  // Handler para cerrar modal con cleanup
  const handleCloseModal = useCallback(() => {
    setSelectedDocs([]);
    setIsInitialized(false);
    onClose();
  }, [onClose, setSelectedDocs]);

  // Handler para descarga con validación
  const handleDownloadClick = useCallback(() => {
    if (selectedDocs.length === 0) {
      console.warn('⚠️ No hay documentos seleccionados para descarga');
      return;
    }
    
    onDownloadAll();
  }, [selectedDocs, onDownloadAll]);

  // Computar estado de selección total
  const totalDocuments = personalDocs.length + othersDocs.length;
  const isAllSelected = totalDocuments > 0 && selectedDocs.length === totalDocuments;
  const hasDocuments = totalDocuments > 0;

  // No renderizar si el modal está cerrado
  if (!open) return null;

  return (
    <div className="multi-download-modal">
      {/* Fondo oscuro que cierra el modal al hacer click */}
      <div className="modal-backdrop" onClick={handleCloseModal}></div>

      <div className="modal-content-preview">
        {/* HEADER - Mantenemos el diseño simple del primer código */}
        <div className="modal-header">
          <div>
            <h2>Descargar Documentos</h2>
            <p className="user-name">{userName}</p>
            {/* Contexto del vehículo simplificado */}
            {vehicleContext && (
              <p className="vehicle-context">
                {vehicleContext.type === 'main' ? 'Vehículo Principal' : 'Vehículo Relacionado'} 
                {vehicleContext.label && ` - ${vehicleContext.label}`}
              </p>
            )}
          </div>
          <button className="close-button" onClick={handleCloseModal}>
            X
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {!hasDocuments ? (
            // Estado vacío
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <h3>No hay documentos disponibles</h3>
              <p>No se encontraron documentos válidos para descargar.</p>
            </div>
          ) : (
            <>
              {/* Columna Izquierda */}
              <div className="column left">
                {/* Seleccionar todo - Mantenemos el diseño simple */}
                <div className="actions">
                  <label>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={(e) => handleToggleSelectAll(e.target.checked)}
                    />
                    Seleccionar Todo
                  </label>
                </div>

                {/* Sistema de Drag & Drop */}
                <DragDropContext onDragEnd={onDragEnd}>
                  <div className="document-list">
                    
                    {/* Documentos Personales - Usamos el nombre del primer código */}
                    {personalDocs.length > 0 && (
                      <div className="section-download">
                        <div
                          className="section-title clickable"
                          onClick={() => setPersonalFilesOpen(!personalFilesOpen)}
                        >
                          <h3>{MODAL_CONFIG.SECTION_LABELS.PERSONAL}</h3>
                        </div>

                        {personalFilesOpen && (
                          <Droppable droppableId="personalDocs">
                            {(provided, snapshot) => (
                              <div
                                className="collapsible-content"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {personalDocs.map((doc, index) => (
                                  <Draggable
                                    key={doc.id}
                                    draggableId={String(doc.id)}
                                    index={index}
                                  >
                                    {(draggableProvided, draggableSnapshot) => (
                                      <div
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                        {...draggableProvided.dragHandleProps}
                                        className={`document-item ${draggableSnapshot.isDragging ? "dragging" : ""}`}
                                      >
                                        <label>
                                          {getFileIcon(doc)}
                                          <input
                                            type="checkbox"
                                            checked={selectedDocs.includes(doc.id)}
                                            onChange={(e) => handleCheckboxChange(doc.id, e.target.checked)}
                                          />
                                          <span className="document-info">
                                            {doc.original_name_description && (
                                              <span className="document-description">
                                                {doc.original_name_description}
                                              </span>
                                            )}
                                          </span>
                                        </label>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        )}
                      </div>
                    )}

                    {/* Otros Documentos - Usamos el nombre del primer código */}
                    {othersDocs.length > 0 && (
                      <div className="section-download">
                        <div
                          className="section-title clickable"
                          onClick={() => setOtherFilesOpen(!otherFilesOpen)}
                        >
                          <h3>{MODAL_CONFIG.SECTION_LABELS.OTHER}</h3>
                        </div>

                        {otherFilesOpen && (
                          <Droppable droppableId="othersDocs">
                            {(provided, snapshot) => (
                              <div
                                className="collapsible-content"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {othersDocs.map((doc, index) => (
                                  <Draggable
                                    key={doc.id}
                                    draggableId={String(doc.id)}
                                    index={index}
                                  >
                                    {(draggableProvided, draggableSnapshot) => (
                                      <div
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                        {...draggableProvided.dragHandleProps}
                                        className={`document-item ${draggableSnapshot.isDragging ? "dragging" : ""}`}
                                      >
                                        <label>
                                          {getFileIcon(doc)}
                                          <input
                                            type="checkbox"
                                            checked={selectedDocs.includes(doc.id)}
                                            onChange={(e) => handleCheckboxChange(doc.id, e.target.checked)}
                                          />
                                          <span className="document-info">
                                            {doc.original_name_description && (
                                              <span className="document-description">
                                                {doc.original_name_description}
                                              </span>
                                            )}
                                          </span>
                                        </label>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        )}
                      </div>
                    )}
                  </div>
                </DragDropContext>
              </div>

              {/* Columna Derecha - Mantenemos el diseño simple */}
              <div className="column right">
                <img
                  src={MelisaImg}
                  alt="Imagen de motivación"
                  className="preview-image"
                />
                <button
                  className="download-button"
                  disabled={selectedDocs.length === 0}
                  onClick={handleDownloadClick}
                >
                  Descargar Seleccionados
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// PropTypes exhaustivos con validación avanzada
MultiDownloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  
  // Arrays de documentos con validación de estructura
  personalDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      file_name: PropTypes.string.isRequired,
      file_path: PropTypes.string.isRequired,
      original_name_description: PropTypes.string,
      document_type_name: PropTypes.string,
      file_type: PropTypes.string
    })
  ),
  
  certificates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      file_name: PropTypes.string.isRequired,
      file_path: PropTypes.string.isRequired,
      original_name_description: PropTypes.string,
      document_type_name: PropTypes.string
    })
  ),
  
  otherDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      file_name: PropTypes.string.isRequired,
      file_path: PropTypes.string.isRequired,
      original_name_description: PropTypes.string,
      document_type_name: PropTypes.string
    })
  ),
  
  // Gestión de selección
  selectedDocs: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedDocs: PropTypes.func.isRequired,
  
  // Handlers
  onDownloadAll: PropTypes.func.isRequired,
  
  // Información de contexto
  userName: PropTypes.string.isRequired,
  isVehicle: PropTypes.bool,
  
  // Contexto vehicular
  vehicleContext: PropTypes.shape({
    type: PropTypes.oneOf(['main', 'related']).isRequired,
    label: PropTypes.string,
    panel: PropTypes.string,
    vehicleInfo: PropTypes.shape({
      plate: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      isTrailer: PropTypes.bool
    })
  })
};

// Valores por defecto seguros
MultiDownloadModal.defaultProps = {
  personalDocuments: [],
  certificates: [],
  otherDocuments: [],
  isVehicle: false,
  vehicleContext: null
};

export default MultiDownloadModal;